import axios from "../../../../api/axios";

import { ServiceStatusEnum, ServiceTypeEnum } from "../../enum/service.enum";
import { JoinProgramResponse } from "../types/program.types";
import { ReviewServiceData } from "../../schemas/review.schema";
import { GetServiceBannersResponse } from "../types/banners.types";
import { ServiceProfileResponse } from "../types/serviceProfile.types";
import { ProgramEquipmentCategory } from "../types/programEquipments.types";
import { CreateProgramFormData, CreateServiceFormData, Service, ServiceRequestDetails } from "../../types/service.type";
import { TopServiceProvidersParams, TopServiceProvidersResponse } from "../types/topServiceProviders.types";
import { ContractResponseData, ContractResponsePayload, CreateContractPayload } from "../types/contract.types";
import {
  GetContractsParams,
  GetOrderedRequestsParams,
  GetReceivedRequestsParams,
  GetContractsResponse,
  GetOrderedRequestsResponse,
  GetReceivedRequestsResponse,
  GetReceivedProgramsResponse,
  GetOrderedProgramsResponse,
} from "../types/requests.types";

export const getServiceBanners = async (): Promise<GetServiceBannersResponse["data"]> => {
  const response = await axios.get<GetServiceBannersResponse>("/api/banners", {
    params: { type: "service" },
  });
  return response.data.data;
};

export const getReceivedRequests = async (params: GetReceivedRequestsParams = {}) => {
  const response = await axios.get<GetReceivedRequestsResponse>("/api/services/received-requests", {
    params: {
      limit: 20,
      offset: 0,
      ...params,
    },
  });
  return response.data;
};

export const getReceivedProgramRequests = async (params: GetReceivedRequestsParams = {}) => {
  const response = await axios.get<GetReceivedProgramsResponse>("/api/services/programs/received-requests", {
    params: {
      limit: 20,
      offset: 0,
      ...params,
    },
  });
  return response.data;
};

export const getOrderedProgramRequests = async (params: GetOrderedRequestsParams = {}) => {
  const response = await axios.get<GetOrderedProgramsResponse>("/api/services/programs/ordered-requests", {
    params: {
      limit: 20,
      offset: 0,
      ...params,
    },
  });
  return response.data;
};

export const getOrderedRequests = async (params: GetOrderedRequestsParams = {}) => {
  const response = await axios.get<GetOrderedRequestsResponse>("/api/services/ordered-requests", {
    params: {
      limit: 20,
      offset: 0,
      ...params,
    },
  });
  return response.data;
};

export const getReceivedContracts = async (params: GetContractsParams) => {
  const response = await axios.get<GetContractsResponse>("/api/services/contracts", {
    params: {
      limit: 20,
      offset: 0,
      serviceOwnerId: params.userId,
      ...params,
    },
  });
  return response.data;
};

export const getOrderedContracts = async (params: GetContractsParams) => {
  const response = await axios.get<GetContractsResponse>("/api/services/contracts", {
    params: {
      limit: 20,
      offset: 0,
      requesterId: params.userId,
      ...params,
    },
  });
  return response.data;
};

export const getServiceProfile = async (userId: number): Promise<ServiceProfileResponse> => {
  const response = await axios.get<ServiceProfileResponse>(`/api/users/${userId}/service-profile`);
  return response.data;
};

export const getServiceProfileForClient = async (userId: number): Promise<ServiceProfileResponse> => {
  const response = await axios.get<ServiceProfileResponse>(`/api/users/${userId}/service-profile-for-client`);
  return response.data;
};

export const getVisitServiceProfile = async (userId: number) => {
  const response = await axios.get<{ data: null; message: string }>(`/api/users/${userId}/visit-service-profile`);
  return response.data;
};

export const getServiceFeedback = async (
  userId?: number,
  limit = 20,
  offset = 0,
  serviceId?: number,
  rating?: number,
  requestId?: number
) => {
  const transformedParams: Record<string, number | undefined> = {};
  if (rating) transformedParams.rating = rating;
  if (requestId) transformedParams.requestId = requestId;
  else if (userId) transformedParams.userId = userId;
  else if (serviceId) transformedParams.serviceId = serviceId;

  const response = await axios.get(`/api/services/feedback`, {
    params: {
      limit,
      offset,
      ...transformedParams,
    },
  });
  return response.data.data;
};

export const getServiceFeedbackForClient = async (userId: number, limit = 20, offset = 0) => {
  const response = await axios.get(`/api/services/feedback-for-client`, {
    params: {
      userId,
      limit,
      offset,
    },
  });
  return response.data.data;
};

export const getUserServices = async (userId: number, limit = 20, offset = 0, type?: ServiceTypeEnum) => {
  const response = await axios.get(`/api/users/${userId}/services`, {
    params: { limit, offset, ...(type && { type }) },
  });
  return response.data.data;
};

export interface TrendingServicesParams {
  limit?: number;
  offset?: number;
  search?: string;
  personas?: string;
  countryIds?: string;
  type?: string;
  categories?: string;
  rating?: number;
  availabilityOption?: string;
}

export const getTrendingServices = async (params?: TrendingServicesParams) => {
  const {
    limit = 20,
    offset = 0,
    search,
    personas,
    countryIds,
    type,
    categories,
    rating,
    availabilityOption,
  } = params || {};
  const response = await axios.get(`/api/services/trending`, {
    params: {
      limit,
      offset,
      ...(search && { search }),
      ...(personas && { personas }),
      ...(countryIds && { countryIds }),
      ...(type && { type }),
      ...(categories && { categories }),
      ...(rating && { rating }),
      ...(availabilityOption && { availabilityOption }),
    },
  });
  return response.data.data;
};

export const getTopServiceProviders = async (
  params?: TopServiceProvidersParams
): Promise<TopServiceProvidersResponse> => {
  const response = await axios.get("/api/services/top-service-providers", {
    params: {
      limit: 20,
      offset: 0,
      ...(params?.countryIds && { countryIds: params.countryIds }),
      ...(params?.type && { type: params.type }),
      ...(params?.categories && { categories: params.categories }),
      ...(params?.rating && { rating: params.rating }),
      ...(params?.availabilityOption && { availabilityOption: params.availabilityOption }),
      ...(params?.role && { personas: params.role }),
      ...params,
    },
  });
  return response.data.data;
};

export const getServiceById = async (serviceId: number): Promise<Service> => {
  const response = await axios.get(`/api/services/${serviceId}`);

  return response.data.data;
};

export const getServiceRequestById = async (requestId: number) => {
  const response = await axios.get<{
    data: ServiceRequestDetails;
    message: string;
  }>(`/api/services/requests/${requestId}`);
  return response.data.data;
};

export const requestService = async (serviceId: number): Promise<{ id: number; message: string }> => {
  const response = await axios.post(`/api/services/requests`, { serviceId });
  return response.data.data;
};

export const cancelServiceRequest = async (
  requestId: number
): Promise<{ id: number; message: string; status: "canceled" }> => {
  const response = await axios.patch(`/api/services/requests/${requestId}/cancel`);
  return response.data.data;
};

export const respondToContract = async (contractId: number, payload: ContractResponsePayload) => {
  const response = await axios.patch<ContractResponseData>(`/api/services/contracts/${contractId}/respond`, payload);
  return response.data;
};

export const getContractDetails = async (contractId: number) => {
  const response = await axios.get<ContractResponseData>(`/api/services/contracts/${contractId}`);
  return response.data.data;
};

export const providerCancelServiceRequest = async (requestId: number) => {
  const response = await axios.patch(`/api/services/requests/${requestId}/response`);
  return response.data.data;
};

export const createContract = async (payload: CreateContractPayload) => {
  const response = await axios.post("/api/services/contracts", payload);
  return response.data;
};

export const sendMilestonePaymentReminder = async (contractId: number) => {
  const response = await axios.patch(`/api/services/contracts/${contractId}/milestone-achieved`);
  return response.data;
};

export const addServiceReview = async (requestId: number, data: ReviewServiceData & { type: ServiceTypeEnum }) => {
  const response = await axios.post(`/api/services/requests/${requestId}/feedback`, data);
  return response.data;
};

export const releaseContractPayment = async (contractId: number) => {
  const response = await axios.patch(`/api/services/contracts/${contractId}/release-payment`);
  return response.data;
};

export const withdrawContract = async (contractId: number) => {
  const response = await axios.post(`/api/services/contracts/${contractId}/withdraw-requests`);
  return response.data;
};

export const joinProgram = async (serviceId: number): Promise<JoinProgramResponse["data"]> => {
  const response = await axios.post<JoinProgramResponse>(`/api/services/programs/${serviceId}/join-requests`);
  return response.data.data;
};

export const createContractPayment = async (contractId: number) => {
  const response = await axios.patch(`/api/services/contracts/${contractId}/create-payment`);
  return response.data.data;
};

export const createService = async (payload: CreateServiceFormData): Promise<Service> => {
  const formattedPayload = {
    ...payload,
    status: ServiceStatusEnum.Published,
    paymentConfig: {
      ...payload.paymentConfig,
      amount: Number(payload.paymentConfig.amount) * 100,
    },
  };
  const response = await axios.post("/api/services", formattedPayload);
  return response.data.data;
};

export const getProgramEquipments = async (): Promise<ProgramEquipmentCategory[]> => {
  const response = await axios.get("/api/services/program-equipments");
  return response.data.data;
};

export const createProgram = async (payload: CreateProgramFormData): Promise<Service> => {
  const formattedPayload = {
    ...payload,
    numberOfVideos: Number(payload.numberOfVideos),
    idealCompletionTime: Number(payload.idealCompletionTime),
    status: ServiceStatusEnum.Published,
    paymentConfig: {
      ...payload.paymentConfig,
      amount: Number(payload.paymentConfig.amount) * 100,
    },
  };
  const response = await axios.post("/api/services/programs", formattedPayload);
  return response.data.data;
};

export const updateServiceStatus = async (
  serviceId: number,
  payload: { status: ServiceStatusEnum }
): Promise<Service> => {
  const response = await axios.patch(`/api/services/${serviceId}/status`, payload);
  return response.data.data;
};

export const deleteService = async (serviceId: number): Promise<void> => {
  await axios.delete(`/api/services/${serviceId}`);
};
